import { t } from '@lingui/core/macro';

import { intlLocale } from '../../lib/locale';
import type { CmdbCardRow } from '../../lib/types';

/** One official version with the custom versions derived from it. */
export interface ProductVersionNode {
  version: CmdbCardRow;
  customVersions: CmdbCardRow[];
}

export interface ProductGroup {
  key: string;
  product: CmdbCardRow | null;
  name: string;
  modelSeries: string;
  /** Official versions, newest first, each with its custom versions. */
  versions: ProductVersionNode[];
  /** Custom versions whose base version is not part of this product. */
  unattachedCustomVersions: CmdbCardRow[];
  /** All versions of the product, custom versions included. */
  versionCount: number;
}

/** Natural, case-insensitive order in the active UI language; rebuilt only when it changes. */
let collator: { locale: string; instance: Intl.Collator } | null = null;
function naturalOrder(): Intl.Collator {
  const locale = intlLocale();
  if (collator?.locale !== locale) {
    collator = { locale, instance: new Intl.Collator(locale, { numeric: true, sensitivity: 'base' }) };
  }
  return collator.instance;
}

function textValue(value: unknown): string {
  return typeof value === 'string' || typeof value === 'number' ? String(value).trim() : '';
}

function field(row: CmdbCardRow, name: string): string {
  return textValue(row.display[name]) || textValue(row.values[name]);
}

function reference(row: CmdbCardRow, name: string): number | null {
  const raw = row.values[name];
  if (typeof raw !== 'number' && typeof raw !== 'string') return null;
  const id = Number(raw);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

export function isCustomVersion(row: CmdbCardRow): boolean {
  return textValue(row.values.VersionType).toUpperCase() === 'CUSTOM';
}

export function productVersionLabel(row: CmdbCardRow): string {
  return textValue(row.values.VersionNr) || t`Ohne Versionsnummer`;
}

/** Numeric descending order ("2.10" before "2.9"); versions without a number go last. */
function newestFirst(left: CmdbCardRow, right: CmdbCardRow): number {
  const a = textValue(left.values.VersionNr);
  const b = textValue(right.values.VersionNr);
  if (!a || !b) return a ? -1 : b ? 1 : left.id - right.id;
  return naturalOrder().compare(b, a) || left.id - right.id;
}

/** Custom versions in the order of their running number (`…-1`, `…-2`, `…-10`). */
function oldestFirst(left: CmdbCardRow, right: CmdbCardRow): number {
  return naturalOrder().compare(textValue(left.values.VersionNr), textValue(right.values.VersionNr))
    || left.id - right.id;
}

/** Keep every version, including records whose product is missing or not assigned. */
export function groupProducts(
  products: readonly CmdbCardRow[],
  versions: readonly CmdbCardRow[],
): ProductGroup[] {
  const groups = new Map<string, { group: ProductGroup; official: CmdbCardRow[]; custom: CmdbCardRow[] }>();
  for (const product of products) {
    const key = `product:${product.id}`;
    groups.set(key, {
      group: {
        key,
        product,
        name: field(product, 'Bezeichnung') || product.label || t`Unbenanntes Produkt`,
        modelSeries: field(product, 'Modellreihe'),
        versions: [],
        unattachedCustomVersions: [],
        versionCount: 0,
      },
      official: [],
      custom: [],
    });
  }

  for (const version of versions) {
    const productId = reference(version, 'ProduktTypeID');
    // A real reference is authoritative, even when its target is absent.
    const key = productId !== null ? `product:${productId}` : 'unassigned';
    let entry = groups.get(key);
    if (!entry) {
      entry = {
        group: {
          key,
          product: null,
          name: productId !== null
            ? field(version, 'ProduktTypeID') || t`Produkt #${productId}`
            : t`Ohne Produktzuordnung`,
          modelSeries: '',
          versions: [],
          unattachedCustomVersions: [],
          versionCount: 0,
        },
        official: [],
        custom: [],
      };
      groups.set(key, entry);
    }
    (isCustomVersion(version) ? entry.custom : entry.official).push(version);
  }

  return [...groups.values()]
    .map(({ group, official, custom }) => {
      const nodes = [...official].sort(newestFirst).map((version) => ({ version, customVersions: [] as CmdbCardRow[] }));
      const byId = new Map(nodes.map((node) => [node.version.id, node]));
      const unattached: CmdbCardRow[] = [];
      for (const version of [...custom].sort(oldestFirst)) {
        const base = reference(version, 'BaseVersionID');
        const node = base === null ? undefined : byId.get(base);
        if (node) node.customVersions.push(version);
        else unattached.push(version);
      }
      return {
        ...group,
        versions: nodes,
        unattachedCustomVersions: unattached,
        versionCount: official.length + custom.length,
      };
    })
    .sort((a, b) => Number(a.product === null) - Number(b.product === null)
      || naturalOrder().compare(a.name, b.name)
      || naturalOrder().compare(a.key, b.key));
}

/** The newest official version with a number, or `null`. */
export function latestOfficialVersion(group: ProductGroup): CmdbCardRow | null {
  return group.versions.find((node) => textValue(node.version.values.VersionNr) !== '')?.version ?? null;
}

/** Form defaults for a new version: fixed product, newest official version as predecessor. */
export function newProductVersionValues(group: ProductGroup): Record<string, unknown> {
  const values: Record<string, unknown> = {};
  if (group.product) values.ProduktTypeID = group.product.id;
  const latest = latestOfficialVersion(group);
  if (latest) values.VorgaengerVersionID = latest.id;
  return values;
}

export function allVersions(group: ProductGroup): CmdbCardRow[] {
  return [
    ...group.versions.flatMap((node) => [node.version, ...node.customVersions]),
    ...group.unattachedCustomVersions,
  ];
}

function rowSearchText(row: CmdbCardRow): string {
  return [row.label, row.code, row.id, ...Object.values(row.display), ...Object.values(row.values)]
    .map(textValue)
    .join(' ');
}

function matches(text: string, terms: readonly string[]): boolean {
  const searchable = text.toLocaleLowerCase(intlLocale());
  return terms.every((term) => searchable.includes(term));
}

function searchTerms(search: string): string[] {
  return search.trim().toLocaleLowerCase(intlLocale()).split(/\s+/).filter(Boolean);
}

/** Matching one version keeps its full product group visible for context. */
export function filterProductGroups(groups: readonly ProductGroup[], search: string): ProductGroup[] {
  const terms = searchTerms(search);
  if (terms.length === 0) return [...groups];
  return groups.filter((group) => matches([
    group.name,
    group.modelSeries,
    group.product ? rowSearchText(group.product) : '',
    ...allVersions(group).map(rowSearchText),
  ].join(' '), terms));
}

/** Groups to open for a search: those where a version (not only the product) matches. */
export function groupsWithMatchingVersions(groups: readonly ProductGroup[], search: string): string[] {
  const terms = searchTerms(search);
  if (terms.length === 0) return [];
  return groups
    .filter((group) => allVersions(group).some((version) => matches(rowSearchText(version), terms)))
    .map((group) => group.key);
}
